import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppStore } from "@/store";
import {
  useGenerateSmartReply,
  useGetReplyHistory,
  useDeleteReply,
  useSubmitVoiceFeedback,
  useGetVoiceProfile,
} from "@workspace/api-client-react";
import type {
  GenerateSmartReplyRequestTone,
  GenerateSmartReplyRequestFormat,
  GenerateSmartReplyRequestReplyLength,
  GenerateSmartReplyRequestHumorLevel,
  GenerateSmartReplyRequestAudience,
  GenerateSmartReplyRequestEmojiUsage,
  GenerateSmartReplyRequestUrgency,
  GenerateSmartReplyRequestFormality,
  GenerateSmartReplyRequestPersonaArchetype,
  GenerateSmartReplyRequestPersonaPowerDynamic,
  GenerateSmartReplyRequestPersonaCommunicationStyle,
  GenerateSmartReplyRequestPersonaFamiliarityLevel,
  SmartReply,
} from "@workspace/api-client-react";

import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { VoiceRecorder } from "@/components/voice-recorder";
import { PaywallModal, usePaywallCheck } from "@/components/paywall-modal";
import {
  Mail,
  Send,
  Copy,
  Check,
  Loader2,
  Trash2,
  ChevronDown,
  ChevronUp,
  Mic,
  Keyboard,
  Pencil,
  History,
  MessageSquare,
  FileText,
  Sparkles,
  Reply,
  PenLine,
  ThumbsUp,
  ThumbsDown,
  SlidersHorizontal,
  Zap,
  ArrowRight,
  X,
  Users,
  MoreHorizontal,
  Plus,
  ChevronLeft,
} from "lucide-react";
import { apiFetch } from "@/lib/api-fetch";
import { formatDistanceToNow } from "date-fns";
import {
  RELATIONSHIP_ARCHETYPES,
  ARCHETYPE_BY_ID,
  POWER_DYNAMIC_LABELS,
  COMMUNICATION_STYLE_LABELS,
  FAMILIARITY_LEVEL_LABELS,
} from "@/data/relationshipArchetypes";
import { useInlineVoiceInput } from "@/hooks/use-inline-voice-input";
import { useIntentDetection } from "@/hooks/use-intent-detection";
import { VoiceInputButton, InlineRecordingIndicator } from "@/components/voice-input-button";

type ComposeMode = "reply" | "compose" | "polish";

const SCENARIO_CHIPS_BY_MODE: Record<ComposeMode, { label: string; prompt: string }[]> = {
  reply: [
    { label: "Say yes",          prompt: "Confirm I'm on board and express genuine enthusiasm" },
    { label: "Decline politely", prompt: "Decline gracefully while leaving the door open" },
    { label: "Ask for more info", prompt: "Ask for more details or clarification before committing" },
    { label: "Push back",        prompt: "Respectfully disagree and share my perspective clearly" },
    { label: "Thank them",       prompt: "Express sincere gratitude without being over the top" },
    { label: "Reschedule",       prompt: "Propose a different time that works better for me" },
  ],
  compose: [
    { label: "Cold outreach",    prompt: "Cold outreach — introduce myself and open the conversation naturally" },
    { label: "Follow up",        prompt: "Follow up politely — check if they had a chance to review" },
    { label: "Schedule a call",  prompt: "Reach out to schedule a call or meeting at a mutual time" },
    { label: "Ask for feedback", prompt: "Ask for honest feedback on something I've shared or done" },
    { label: "Pitch an idea",    prompt: "Pitch my idea in a compelling but low-pressure way" },
    { label: "Check in",         prompt: "Reach out to see how they're doing and reconnect warmly" },
  ],
  polish: [
    { label: "More concise",     prompt: "Make it more concise — cut the fluff and sharpen the key points" },
    { label: "Add warmth",       prompt: "Add more warmth and personality — make it feel genuinely human" },
    { label: "Sound confident",  prompt: "Make it sound more confident and decisive, less hedging" },
    { label: "Less formal",      prompt: "Make it less formal and more conversational" },
    { label: "Add a CTA",        prompt: "Add a clear and natural call to action at the end" },
    { label: "Stronger hook",    prompt: "Strengthen the opening line to grab attention immediately" },
  ],
};

const LS_TONE_KEY   = "scribpost-smart-tone";
const LS_FORMAT_KEY = "scribpost-smart-format";
const LS_LENGTH_KEY = "scribpost-smart-length";

const VALID_TONE_VALUES   = ["professional", "friendly", "casual", "assertive", "empathetic", "concise"] as const;
const VALID_FORMAT_VALUES = ["email", "chat", "formal_letter"] as const;
const VALID_LENGTH_VALUES = ["short", "medium", "long"] as const;

function readLs<T>(key: string, fallback: T, allowedValues?: readonly unknown[]): T {
  try {
    const v = localStorage.getItem(key);
    if (!v) return fallback;
    const parsed = JSON.parse(v) as unknown;
    if (allowedValues && !allowedValues.includes(parsed)) return fallback;
    return parsed as T;
  } catch {
    return fallback;
  }
}

type ToneEntry = { value: GenerateSmartReplyRequestTone; label: string; emoji: string };

type ToneContextKey =
  | "reply-email" | "reply-chat" | "reply-formal_letter"
  | "compose-email" | "compose-chat" | "compose-formal_letter"
  | "polish";

const TONE_CONTEXT_MAP: Record<ToneContextKey, ToneEntry[]> = {
  "reply-email": [
    { value: "professional", label: "Formal",        emoji: "💼" },
    { value: "friendly",     label: "Warm",          emoji: "🤝" },
    { value: "casual",       label: "Personal",      emoji: "😊" },
    { value: "assertive",    label: "Firm",          emoji: "💪" },
    { value: "empathetic",   label: "Understanding", emoji: "❤️" },
    { value: "concise",      label: "Brief",         emoji: "⚡" },
  ],
  "reply-chat": [
    { value: "professional", label: "Clear",    emoji: "✅" },
    { value: "friendly",     label: "Friendly", emoji: "😊" },
    { value: "casual",       label: "Chill",    emoji: "✌️" },
    { value: "assertive",    label: "Direct",   emoji: "🎯" },
    { value: "empathetic",   label: "Caring",   emoji: "🤝" },
    { value: "concise",      label: "Quick",    emoji: "⚡" },
  ],
  "reply-formal_letter": [
    { value: "professional", label: "Formal",       emoji: "📄" },
    { value: "friendly",     label: "Courteous",    emoji: "🤝" },
    { value: "casual",       label: "Approachable", emoji: "😊" },
    { value: "assertive",    label: "Confident",    emoji: "💪" },
    { value: "empathetic",   label: "Considerate",  emoji: "❤️" },
    { value: "concise",      label: "Concise",      emoji: "⚡" },
  ],
  "compose-email": [
    { value: "professional", label: "Persuasive",    emoji: "🎯" },
    { value: "friendly",     label: "Warm",          emoji: "😊" },
    { value: "casual",       label: "Conversational", emoji: "💬" },
    { value: "assertive",    label: "Confident",     emoji: "💪" },
    { value: "empathetic",   label: "Inviting",      emoji: "🤝" },
    { value: "concise",      label: "Punchy",        emoji: "⚡" },
  ],
  "compose-chat": [
    { value: "professional", label: "Polished",   emoji: "✨" },
    { value: "friendly",     label: "Friendly",   emoji: "😊" },
    { value: "casual",       label: "Casual",     emoji: "👋" },
    { value: "assertive",    label: "Bold",       emoji: "🎯" },
    { value: "empathetic",   label: "Engaging",   emoji: "🤝" },
    { value: "concise",      label: "Snappy",     emoji: "⚡" },
  ],
  "compose-formal_letter": [
    { value: "professional", label: "Formal",      emoji: "📄" },
    { value: "friendly",     label: "Respectful",  emoji: "🤝" },
    { value: "casual",       label: "Personable",  emoji: "😊" },
    { value: "assertive",    label: "Decisive",    emoji: "💪" },
    { value: "empathetic",   label: "Thoughtful",  emoji: "❤️" },
    { value: "concise",      label: "Direct",      emoji: "⚡" },
  ],
  polish: [
    { value: "professional", label: "More Polished",    emoji: "✨" },
    { value: "friendly",     label: "Warmer",           emoji: "😊" },
    { value: "casual",       label: "More Natural",     emoji: "👋" },
    { value: "assertive",    label: "More Confident",   emoji: "💪" },
    { value: "empathetic",   label: "More Human",       emoji: "🤝" },
    { value: "concise",      label: "Tighter",          emoji: "⚡" },
  ],
};

const FORMATS: { value: GenerateSmartReplyRequestFormat; label: string; icon: typeof Mail }[] = [
  { value: "email",        label: "Email",         icon: Mail },
  { value: "chat",         label: "Chat / Text",   icon: MessageSquare },
  { value: "formal_letter",label: "Formal Letter", icon: FileText },
];

interface Persona {
  id: string;
  name: string;
  relationship: string | null;
  notes: string | null;
  settings: {
    tone?: string; format?: string; replyLength?: string;
    humorLevel?: string; audience?: string; emojiUsage?: string;
    urgency?: string; formality?: string;
    customInstruction?: string; targetWordCount?: number;
  };
  archetype: GenerateSmartReplyRequestPersonaArchetype | null;
  powerDynamic: GenerateSmartReplyRequestPersonaPowerDynamic | null;
  communicationStyle: GenerateSmartReplyRequestPersonaCommunicationStyle | null;
  familiarityLevel: GenerateSmartReplyRequestPersonaFamiliarityLevel | null;
  avoidTopics: string | null;
  usageCount: number;
  lastUsedAt: string | null;
}

type PersonaModalStep = "archetype" | "details";

export default function SmartReplyPage() {
  const { sessionId, authToken } = useAppStore();
  const { toast } = useToast();
  const intentRef = useRef<HTMLTextAreaElement>(null);

  const [mode, setMode]                     = useState<ComposeMode>("reply");
  const [originalMessage, setOriginalMessage] = useState("");
  const [polishDraft, setPolishDraft]       = useState("");
  const [intentNote, setIntentNote]         = useState("");
  const [tone, setTone]                     = useState<GenerateSmartReplyRequestTone>(
    () => readLs<GenerateSmartReplyRequestTone>(LS_TONE_KEY, "professional", VALID_TONE_VALUES)
  );
  const [format, setFormat]                 = useState<GenerateSmartReplyRequestFormat>(
    () => readLs<GenerateSmartReplyRequestFormat>(LS_FORMAT_KEY, "email", VALID_FORMAT_VALUES)
  );
  const [replyLength, setReplyLength]       = useState<GenerateSmartReplyRequestReplyLength>(
    () => readLs<GenerateSmartReplyRequestReplyLength>(LS_LENGTH_KEY, "medium", VALID_LENGTH_VALUES)
  );

  useEffect(() => { try { localStorage.setItem(LS_TONE_KEY,   JSON.stringify(tone));   } catch {} }, [tone]);
  useEffect(() => { try { localStorage.setItem(LS_FORMAT_KEY, JSON.stringify(format)); } catch {} }, [format]);
  useEffect(() => { try { localStorage.setItem(LS_LENGTH_KEY, JSON.stringify(replyLength)); } catch {} }, [replyLength]);
  const [result, setResult]                 = useState<SmartReply | null>(null);
  const [editedReply, setEditedReply]       = useState("");
  const [isEditing, setIsEditing]           = useState(false);
  const [copied, setCopied]                 = useState(false);
  const [inputMode, setInputMode]           = useState<"text" | "voice">("text");
  const [historyOpen, setHistoryOpen]       = useState(false);
  const [advancedOpen, setAdvancedOpen]     = useState(false);
  const [humorLevel, setHumorLevel]         = useState<GenerateSmartReplyRequestHumorLevel | undefined>(undefined);
  const [audience, setAudience]             = useState<GenerateSmartReplyRequestAudience | undefined>(undefined);
  const [emojiUsage, setEmojiUsage]         = useState<GenerateSmartReplyRequestEmojiUsage | undefined>(undefined);
  const [urgency, setUrgency]               = useState<GenerateSmartReplyRequestUrgency | undefined>(undefined);
  const [formality, setFormality]           = useState<GenerateSmartReplyRequestFormality | undefined>(undefined);
  const [replyCustomInstruction, setReplyCustomInstruction] = useState<string>("");
  const [replyTargetWordCount, setReplyTargetWordCount]     = useState<string>("");
  const [feedbackGiven, setFeedbackGiven]   = useState<"up" | "down" | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [feedbackDismissed, setFeedbackDismissed] = useState(false);

  const [savedPersonas, setSavedPersonas]         = useState<Persona[]>([]);
  const [activePersonaId, setActivePersonaId]     = useState<string | null>(null);
  const [personaMenuOpenId, setPersonaMenuOpenId] = useState<string | null>(null);

  const [personaModalOpen, setPersonaModalOpen]   = useState(false);
  const [personaModalStep, setPersonaModalStep]   = useState<PersonaModalStep>("archetype");
  const [editingPersonaId, setEditingPersonaId]   = useState<string | null>(null);
  const [personaName, setPersonaName]             = useState("");
  const [personaRelationship, setPersonaRelationship] = useState("");
  const [personaNotes, setPersonaNotes]           = useState("");
  const [personaArchetype, setPersonaArchetype]   = useState<GenerateSmartReplyRequestPersonaArchetype | null>(null);
  const [personaPowerDynamic, setPersonaPowerDynamic]         = useState<GenerateSmartReplyRequestPersonaPowerDynamic | null>(null);
  const [personaCommunicationStyle, setPersonaCommunicationStyle] = useState<GenerateSmartReplyRequestPersonaCommunicationStyle | null>(null);
  const [personaFamiliarityLevel, setPersonaFamiliarityLevel] = useState<GenerateSmartReplyRequestPersonaFamiliarityLevel | null>(null);
  const [personaAvoidTopics, setPersonaAvoidTopics]           = useState("");
  const [personaSaving, setPersonaSaving]         = useState(false);
  const [modalTone, setModalTone]                 = useState<GenerateSmartReplyRequestTone>("professional");
  const [modalFormat, setModalFormat]             = useState<GenerateSmartReplyRequestFormat>("email");
  const [modalReplyLength, setModalReplyLength]   = useState<GenerateSmartReplyRequestReplyLength>("medium");

  const activePersonaIdRef = useRef<string | null>(null);
  useEffect(() => { activePersonaIdRef.current = activePersonaId; }, [activePersonaId]);

  const { showPaywall, checkPaywall, dismissPaywall } = usePaywallCheck();
  const [showHardPaywall, setShowHardPaywall] = useState(false);

  const isCompose = mode === "compose";
  const isPolish  = mode === "polish";

  const scenarioChips = SCENARIO_CHIPS_BY_MODE[mode];
  const contextKey: ToneContextKey = isPolish ? "polish" : `${mode}-${format}` as ToneContextKey;
  const activeTones: ToneEntry[] = TONE_CONTEXT_MAP[contextKey] ?? TONE_CONTEXT_MAP["reply-email"];

  const sessionHeaders = { headers: { "x-session-id": sessionId } };

  const { data: voiceProfile } = useGetVoiceProfile({ request: sessionHeaders });
  const hasPreferences = !!(voiceProfile?.preferences && Object.keys(voiceProfile.preferences as object).length > 0);
  const authHeaders = useMemo(() => ({
    headers: {
      "x-session-id": sessionId,
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
  }), [sessionId, authToken]);

  const activePersona = useMemo(() => savedPersonas.find(p => p.id === activePersonaId) ?? null, [savedPersonas, activePersonaId]);

  const generateMutation = useGenerateSmartReply({
    mutation: {
      onSuccess: (data) => {
        setResult(data.reply);
        setEditedReply(data.reply.generatedReply);
        setIsEditing(false);
        setFeedbackGiven(null);
        setFeedbackMessage(null);
        setFeedbackDismissed(false);
        refetchHistory();
        if (activePersonaIdRef.current) {
          void recordPersonaUse(activePersonaIdRef.current);
        }
      },
      onError: (err: Error) => {
        const msg = err?.message || "Failed to generate reply";
        if (/free limit|subscribe|subscription required/i.test(msg)) {
          setShowHardPaywall(true);
          return;
        }
        toast({ title: "Generation failed", description: msg, variant: "destructive" });
      },
    },
    request: authHeaders,
  });

  const feedbackMutation = useSubmitVoiceFeedback({
    mutation: {
      onSuccess: (data) => setFeedbackMessage(data.message),
      onError:   ()     => setFeedbackMessage("Feedback recorded"),
    },
    request: { headers: { "x-session-id": sessionId } },
  });

  const { data: historyData, refetch: refetchHistory } = useGetReplyHistory(
    { limit: 50, offset: 0 },
    { request: sessionHeaders }
  );

  const deleteMutation = useDeleteReply({
    mutation: {
      onSuccess: () => { toast({ title: "Reply deleted" }); refetchHistory(); },
    },
    request: sessionHeaders,
  });

  const canGenerate = isPolish
    ? !!polishDraft.trim()
    : isCompose
    ? !!intentNote.trim()
    : !!originalMessage.trim() && !!intentNote.trim();

  const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");

  const transcribeAudioInline = useCallback(
    async (audioBase64: string) => {
      const res = await apiFetch(`${baseUrl}/api/voice/transcribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-session-id": sessionId },
        body: JSON.stringify({ audioBase64, sessionId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? "Transcription failed");
      }
      return res.json() as Promise<{ text: string }>;
    },
    [baseUrl, sessionId]
  );

  const { detectIntent } = useIntentDetection({ sessionId, baseUrl });

  const handleIntentVoiceTranscript = useCallback(
    async (text: string) => {
      const intent = await detectIntent(text);
      const displayText = intent?.displayText || text;
      if (intent?.field === "message") {
        if (!isCompose) setOriginalMessage(displayText);
        else setIntentNote(displayText);
      } else {
        setIntentNote(displayText);
      }
    },
    [detectIntent, isCompose]
  );

  const intentVoiceInput = useInlineVoiceInput({
    onTranscript: handleIntentVoiceTranscript,
    transcribeAudio: transcribeAudioInline,
  });

  const originalMessageVoiceInput = useInlineVoiceInput({
    onTranscript: (text) => setOriginalMessage(text),
    transcribeAudio: transcribeAudioInline,
  });

  const handleGenerate = () => {
    if (!canGenerate) return;
    if (checkPaywall()) return;
    setResult(null);
    const parsedWC = parseInt(replyTargetWordCount, 10);
    const wordCountParam = !isNaN(parsedWC) && parsedWC >= 5 ? parsedWC : undefined;
    const advancedParams = {
      humorLevel, audience, emojiUsage, urgency, formality,
      customInstruction: replyCustomInstruction.trim() || undefined,
      targetWordCount: wordCountParam,
    };

    const personaParams = activePersona ? {
      personaName: activePersona.name || undefined,
      personaArchetype: activePersona.archetype || undefined,
      personaPowerDynamic: activePersona.powerDynamic || undefined,
      personaCommunicationStyle: activePersona.communicationStyle || undefined,
      personaFamiliarityLevel: activePersona.familiarityLevel || undefined,
      personaAvoidTopics: activePersona.avoidTopics || undefined,
    } : {};

    if (isPolish) {
      const polishDirectionNote = intentNote.trim() || "";
      const polishPayload = {
        originalMessage: polishDraft,
        intentNote: polishDirectionNote || "Voice-align this draft to sound exactly like me.",
        tone, format, replyLength,
        polishMode: true,
        ...advancedParams,
        ...personaParams,
      };
      generateMutation.mutate({ data: polishPayload });
    } else {
      generateMutation.mutate({
        data: {
          originalMessage: isCompose ? "" : originalMessage,
          intentNote, tone, format, replyLength,
          ...advancedParams,
          ...personaParams,
        },
      });
    }
  };

  const handleFeedback = (rating: "up" | "down") => {
    if (!result || feedbackGiven) return;
    setFeedbackGiven(rating);
    feedbackMutation.mutate({
      data: result.id
        ? { replyId: result.id, rating }
        : { content: editedReply || result.generatedReply, rating },
    });
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: "Copied to clipboard!" });
  };

  const handleVoiceTranscription = (text: string) => {
    setIntentNote(text);
    setInputMode("text");
  };

  const fetchPersonas = useCallback(async () => {
    try {
      const res = await apiFetch(`${baseUrl}/api/reply/personas`, {
        headers: { "x-session-id": sessionId },
      });
      if (res.ok) {
        const data = await res.json() as { personas: Persona[] };
        setSavedPersonas(data.personas);
      }
    } catch {}
  }, [baseUrl, sessionId]);

  useEffect(() => { fetchPersonas(); }, [fetchPersonas]);

  const handleLoadPersona = useCallback((persona: Persona) => {
    setActivePersonaId(persona.id);
    const s = persona.settings;
    if (s.tone) setTone(s.tone as GenerateSmartReplyRequestTone);
    if (s.format) setFormat(s.format as GenerateSmartReplyRequestFormat);
    if (s.replyLength) setReplyLength(s.replyLength as GenerateSmartReplyRequestReplyLength);
    setHumorLevel(s.humorLevel as GenerateSmartReplyRequestHumorLevel | undefined);
    setAudience(s.audience as GenerateSmartReplyRequestAudience | undefined);
    setEmojiUsage(s.emojiUsage as GenerateSmartReplyRequestEmojiUsage | undefined);
    setUrgency(s.urgency as GenerateSmartReplyRequestUrgency | undefined);
    setFormality(s.formality as GenerateSmartReplyRequestFormality | undefined);
    setReplyCustomInstruction(s.customInstruction ?? "");
    setReplyTargetWordCount(s.targetWordCount ? String(s.targetWordCount) : "");
    setPersonaMenuOpenId(null);
  }, []);

  const recordPersonaUse = useCallback(async (id: string) => {
    try {
      const res = await apiFetch(`${baseUrl}/api/reply/personas/${id}/use`, {
        method: "POST",
        headers: { "x-session-id": sessionId },
      });
      if (res.ok) await fetchPersonas();
    } catch {}
  }, [baseUrl, sessionId, fetchPersonas]);

  const openNewPersonaModal = useCallback(() => {
    setEditingPersonaId(null);
    setPersonaName("");
    setPersonaRelationship("");
    setPersonaNotes("");
    setPersonaArchetype(null);
    setPersonaPowerDynamic(null);
    setPersonaCommunicationStyle(null);
    setPersonaFamiliarityLevel(null);
    setPersonaAvoidTopics("");
    setModalTone(tone);
    setModalFormat(format);
    setModalReplyLength(replyLength);
    setPersonaModalStep("archetype");
    setPersonaModalOpen(true);
    setPersonaMenuOpenId(null);
  }, [tone, format, replyLength]);

  const openEditPersonaModal = useCallback((persona: Persona) => {
    setEditingPersonaId(persona.id);
    setPersonaName(persona.name);
    setPersonaRelationship(persona.relationship ?? "");
    setPersonaNotes(persona.notes ?? "");
    setPersonaArchetype(persona.archetype);
    setPersonaPowerDynamic(persona.powerDynamic);
    setPersonaCommunicationStyle(persona.communicationStyle);
    setPersonaFamiliarityLevel(persona.familiarityLevel);
    setPersonaAvoidTopics(persona.avoidTopics ?? "");
    const s = persona.settings;
    setModalTone((s.tone as GenerateSmartReplyRequestTone) || "professional");
    setModalFormat((s.format as GenerateSmartReplyRequestFormat) || "email");
    setModalReplyLength((s.replyLength as GenerateSmartReplyRequestReplyLength) || "medium");
    setPersonaModalStep("details");
    setPersonaModalOpen(true);
    setPersonaMenuOpenId(null);
  }, []);

  const handleSelectArchetype = useCallback((archetypeId: string) => {
    const archetype = ARCHETYPE_BY_ID[archetypeId];
    if (!archetype) return;
    setPersonaArchetype(archetypeId as GenerateSmartReplyRequestPersonaArchetype);
    setPersonaPowerDynamic(archetype.defaultPowerDynamic as GenerateSmartReplyRequestPersonaPowerDynamic);
    setPersonaCommunicationStyle(archetype.defaultCommunicationStyle as GenerateSmartReplyRequestPersonaCommunicationStyle);
    setPersonaFamiliarityLevel(archetype.defaultFamiliarityLevel as GenerateSmartReplyRequestPersonaFamiliarityLevel);
    const s = archetype.defaultSettings;
    if (s.tone) setModalTone(s.tone as GenerateSmartReplyRequestTone);
    if (s.format) setModalFormat(s.format as GenerateSmartReplyRequestFormat);
    if (s.replyLength) setModalReplyLength(s.replyLength as GenerateSmartReplyRequestReplyLength);
    if (s.formality) setFormality(s.formality as GenerateSmartReplyRequestFormality);
    setPersonaModalStep("details");
  }, []);

  const handleSavePersona = useCallback(async () => {
    const trimmedName = personaName.trim();
    if (!trimmedName) return;
    setPersonaSaving(true);
    try {
      const settings = {
        tone: modalTone, format: modalFormat, replyLength: modalReplyLength,
        humorLevel, audience, emojiUsage, urgency, formality,
        customInstruction: replyCustomInstruction.trim() || undefined,
        targetWordCount: replyTargetWordCount.trim() ? parseInt(replyTargetWordCount, 10) : undefined,
      };
      const body = {
        name: trimmedName,
        relationship: personaRelationship.trim() || undefined,
        notes: personaNotes.trim() || undefined,
        settings,
        archetype: personaArchetype || undefined,
        powerDynamic: personaPowerDynamic || undefined,
        communicationStyle: personaCommunicationStyle || undefined,
        familiarityLevel: personaFamiliarityLevel || undefined,
        avoidTopics: personaAvoidTopics.trim() || undefined,
      };
      const url = editingPersonaId
        ? `${baseUrl}/api/reply/personas/${editingPersonaId}`
        : `${baseUrl}/api/reply/personas`;
      const res = await apiFetch(url, {
        method: editingPersonaId ? "PUT" : "POST",
        headers: { "x-session-id": sessionId, "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json() as { error?: string };
        toast({ title: "Could not save persona", description: err.error, variant: "destructive" });
        return;
      }
      await fetchPersonas();
      setPersonaModalOpen(false);
      setEditingPersonaId(null);
      toast({ title: editingPersonaId ? "Person updated" : "Person saved", description: trimmedName });
    } catch {
      toast({ title: "Could not save persona", variant: "destructive" });
    } finally {
      setPersonaSaving(false);
    }
  }, [personaName, personaRelationship, personaNotes, editingPersonaId, baseUrl, sessionId, modalTone, modalFormat, modalReplyLength, humorLevel, audience, emojiUsage, urgency, formality, replyCustomInstruction, replyTargetWordCount, personaArchetype, personaPowerDynamic, personaCommunicationStyle, personaFamiliarityLevel, personaAvoidTopics, fetchPersonas, toast]);

  const handleDeletePersona = useCallback(async (id: string, name: string) => {
    setPersonaMenuOpenId(null);
    try {
      const res = await apiFetch(`${baseUrl}/api/reply/personas/${id}`, {
        method: "DELETE",
        headers: { "x-session-id": sessionId },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        toast({ title: "Could not delete persona", description: err.error, variant: "destructive" });
        return;
      }
      if (activePersonaId === id) setActivePersonaId(null);
      await fetchPersonas();
      toast({ title: `Deleted: ${name}` });
    } catch {
      toast({ title: "Could not delete persona", variant: "destructive" });
    }
  }, [baseUrl, sessionId, activePersonaId, fetchPersonas, toast]);

  const handleHistoryItemClick = (reply: SmartReply) => {
    setResult(reply);
    setEditedReply(reply.generatedReply);
    setIsEditing(false);
  };

  const isLoading = generateMutation.isPending;
  const voiceSampleCount = voiceProfile?.sampleCount ?? 0;
  const [voiceNudgeDismissed, setVoiceNudgeDismissed] = useState(false);

  return (
    <>
      <PaywallModal show={showPaywall} onDismiss={dismissPaywall} />
      <PaywallModal show={showHardPaywall} onDismiss={() => {}} hard />

      {/* Persona Creation / Edit Modal */}
      <AnimatePresence>
        {personaModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.6)" }}
            onClick={(e) => { if (e.target === e.currentTarget) setPersonaModalOpen(false); }}
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              className="bg-card border border-border rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
            >
              {/* Modal header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                  {personaModalStep === "details" && !editingPersonaId && (
                    <button
                      onClick={() => setPersonaModalStep("archetype")}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                  )}
                  <h2 className="font-display font-bold text-base">
                    {personaModalStep === "archetype"
                      ? "Who are you writing to?"
                      : editingPersonaId
                      ? "Edit person"
                      : "Add person"}
                  </h2>
                </div>
                <button
                  onClick={() => setPersonaModalOpen(false)}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Step 1: Archetype Picker */}
              {personaModalStep === "archetype" && (
                <div className="p-6">
                  <p className="text-sm text-muted-foreground mb-4">
                    Pick a relationship type — we'll pre-fill smart defaults you can adjust.
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {RELATIONSHIP_ARCHETYPES.map((arch) => (
                      <button
                        key={arch.id}
                        onClick={() => handleSelectArchetype(arch.id)}
                        className="flex flex-col items-center gap-1.5 p-3 rounded-2xl border border-border bg-background hover:border-primary/40 hover:bg-primary/5 transition-all text-center group"
                      >
                        <span className="text-2xl">{arch.emoji}</span>
                        <span className="text-xs font-semibold text-foreground group-hover:text-primary transition-colors leading-tight">
                          {arch.label}
                        </span>
                        <span className="text-[10px] text-muted-foreground leading-tight">{arch.description}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setPersonaModalStep("details")}
                    className="mt-4 w-full text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
                  >
                    Skip — I'll fill in details manually
                  </button>
                </div>
              )}

              {/* Step 2: Details Form */}
              {personaModalStep === "details" && (
                <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                  {personaArchetype && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-primary/8 border border-primary/20">
                      <span className="text-lg">{ARCHETYPE_BY_ID[personaArchetype]?.emoji}</span>
                      <div>
                        <p className="text-xs font-semibold text-primary">{ARCHETYPE_BY_ID[personaArchetype]?.label}</p>
                        <button
                          onClick={() => { setPersonaArchetype(null); setPersonaModalStep("archetype"); }}
                          className="text-[10px] text-muted-foreground hover:text-foreground transition-colors underline underline-offset-1"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                      Name <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Sarah, My Boss, Dr. Chen"
                      value={personaName}
                      onChange={(e) => setPersonaName(e.target.value.slice(0, 60))}
                      autoFocus
                      className="w-full text-sm bg-background border border-border rounded-xl px-3 py-2.5 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                      Power dynamic
                    </label>
                    <div className="flex gap-2">
                      {(Object.entries(POWER_DYNAMIC_LABELS) as [string, string][]).map(([val, label]) => (
                        <button
                          key={val}
                          onClick={() => setPersonaPowerDynamic(personaPowerDynamic === val ? null : val as GenerateSmartReplyRequestPersonaPowerDynamic)}
                          className={`flex-1 py-2 px-2 rounded-xl text-xs font-medium border transition-all text-center ${
                            personaPowerDynamic === val
                              ? "bg-primary/10 border-primary/40 text-primary"
                              : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                      Communication style
                    </label>
                    <div className="flex gap-2">
                      {(Object.entries(COMMUNICATION_STYLE_LABELS) as [string, string][]).map(([val, label]) => (
                        <button
                          key={val}
                          onClick={() => setPersonaCommunicationStyle(personaCommunicationStyle === val ? null : val as GenerateSmartReplyRequestPersonaCommunicationStyle)}
                          className={`flex-1 py-2 px-2 rounded-xl text-xs font-medium border transition-all text-center ${
                            personaCommunicationStyle === val
                              ? "bg-primary/10 border-primary/40 text-primary"
                              : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                      How well do you know them?
                    </label>
                    <div className="flex gap-2">
                      {(Object.entries(FAMILIARITY_LEVEL_LABELS) as [string, string][]).map(([val, label]) => (
                        <button
                          key={val}
                          onClick={() => setPersonaFamiliarityLevel(personaFamiliarityLevel === val ? null : val as GenerateSmartReplyRequestPersonaFamiliarityLevel)}
                          className={`flex-1 py-2 px-2 rounded-xl text-xs font-medium border transition-all text-center ${
                            personaFamiliarityLevel === val
                              ? "bg-primary/10 border-primary/40 text-primary"
                              : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                      What to avoid
                    </label>
                    <textarea
                      placeholder="e.g. small talk, jokes, discussing budgets, being too casual"
                      value={personaAvoidTopics}
                      onChange={(e) => setPersonaAvoidTopics(e.target.value.slice(0, 300))}
                      rows={2}
                      className="w-full text-sm bg-background border border-border rounded-xl px-3 py-2.5 text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
                    />
                    <p className="text-[10px] text-muted-foreground text-right mt-0.5">{personaAvoidTopics.length}/300</p>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                      Notes <span className="text-muted-foreground/60 font-normal">(optional)</span>
                    </label>
                    <textarea
                      placeholder="e.g. Prefers bullet points, always busy, very formal in emails"
                      value={personaNotes}
                      onChange={(e) => setPersonaNotes(e.target.value.slice(0, 300))}
                      rows={2}
                      className="w-full text-sm bg-background border border-border rounded-xl px-3 py-2.5 text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
                    />
                  </div>

                  <div className="border-t border-border/60 pt-4 space-y-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tone, Format & Length</p>

                    <div>
                      <label className="text-xs text-muted-foreground mb-1.5 block">Tone</label>
                      <div className="flex flex-wrap gap-1.5">
                        {(["professional","friendly","casual","assertive","empathetic","concise"] as GenerateSmartReplyRequestTone[]).map((t) => (
                          <button
                            key={t}
                            onClick={() => setModalTone(t)}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all capitalize ${modalTone === t ? "bg-primary/10 border-primary/40 text-primary" : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"}`}
                          >{t}</button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-muted-foreground mb-1.5 block">Format</label>
                      <div className="flex gap-2">
                        {([["email","Email"],["chat","Chat"],["formal_letter","Formal letter"]] as [GenerateSmartReplyRequestFormat, string][]).map(([val, label]) => (
                          <button
                            key={val}
                            onClick={() => setModalFormat(val)}
                            className={`flex-1 py-1.5 px-2 rounded-xl text-xs font-medium border transition-all text-center ${modalFormat === val ? "bg-primary/10 border-primary/40 text-primary" : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"}`}
                          >{label}</button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-muted-foreground mb-1.5 block">Length</label>
                      <div className="flex gap-2">
                        {([["short","Short","1–3 sentences"],["medium","Medium","1–2 paragraphs"],["long","Long","Full response"]] as [GenerateSmartReplyRequestReplyLength, string, string][]).map(([val, label, desc]) => (
                          <button
                            key={val}
                            onClick={() => setModalReplyLength(val)}
                            className={`flex-1 flex flex-col items-center px-2 py-2 rounded-xl text-center border transition-all ${modalReplyLength === val ? "bg-primary/10 border-primary/40 text-primary" : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"}`}
                          >
                            <span className="text-xs font-semibold">{label}</span>
                            <span className="text-[10px] opacity-60 mt-0.5">{desc}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={handleSavePersona}
                      disabled={!personaName.trim() || personaSaving}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-primary text-primary-foreground disabled:opacity-40 hover:opacity-90 transition-all"
                    >
                      {personaSaving ? "Saving…" : editingPersonaId ? "Update person" : "Save person"}
                    </button>
                    <button
                      onClick={() => setPersonaModalOpen(false)}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-5xl mx-auto space-y-6"
      >
        {/* Voice training nudge */}
        {voiceSampleCount < 3 && !voiceNudgeDismissed && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 px-4 py-3 rounded-xl border border-primary/40"
            style={{ background: "linear-gradient(135deg, hsl(188 90% 42% / 0.08), hsl(265 75% 62% / 0.05))", boxShadow: "0 0 18px 2px hsl(188 90% 42% / 0.12), inset 0 0 0 1px hsl(188 90% 42% / 0.1)" }}
            data-testid="voice-nudge-banner"
          >
            <Sparkles className="w-4 h-4 text-primary shrink-0" />
            <p className="text-sm text-foreground flex-1">
              <span className="font-semibold">Your voice isn't quite unique yet</span>
              {" "}— add a sample for better results.
            </p>
            <button
              className="shrink-0 text-primary hover:text-primary/80 font-semibold text-sm flex items-center gap-1 px-2 py-1"
              onClick={() => window.open("/training", "_blank")}
            >
              Train now <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setVoiceNudgeDismissed(true)}
              className="shrink-0 text-muted-foreground hover:text-foreground transition-colors p-1"
              aria-label="Dismiss voice training banner"
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-display font-bold flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-primary" />
                </div>
                Smart Message
              </h1>
              {hasPreferences && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/25 text-primary text-xs font-semibold">
                  <Check className="w-3 h-3" /> Voice Preferences active
                </span>
              )}
            </div>
            <p className="text-muted-foreground mt-1 ml-[52px]">
              {isPolish
                ? "Paste your rough draft — we'll polish it to sound exactly like you."
                : "Paste any message, say what you want to say — get a polished reply in your voice."}
            </p>
          </div>

          {/* Mode toggle — prominent */}
          <div className="flex items-center gap-1 bg-card border border-border rounded-2xl p-1 shadow-sm shrink-0">
            <button
              onClick={() => setMode("reply")}
              data-testid="mode-reply"
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                mode === "reply"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Reply className="w-4 h-4" /> Reply
            </button>
            <button
              onClick={() => setMode("compose")}
              data-testid="mode-compose"
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                mode === "compose"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <PenLine className="w-4 h-4" /> Compose
            </button>
            <button
              onClick={() => setMode("polish")}
              data-testid="mode-polish"
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                mode === "polish"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Sparkles className="w-4 h-4" /> Polish
            </button>
          </div>
        </div>

        {/* ── People / Personas panel — always visible at top ── */}
        <div className="bg-card border border-border rounded-3xl px-5 py-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> People
            </label>
            {activePersonaId && (
              <button
                onClick={() => setActivePersonaId(null)}
                className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
              >
                Clear selection
              </button>
            )}
          </div>

          {/* Horizontally scrollable card row */}
          <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-none" style={{ scrollbarWidth: "none" }}>
            {/* "No person" card */}
            <button
              onClick={() => setActivePersonaId(null)}
              className={`shrink-0 flex flex-col items-center gap-1 px-3 py-2.5 rounded-2xl border transition-all min-w-[72px] ${
                activePersonaId === null
                  ? "bg-primary/10 border-primary/50 text-primary"
                  : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
              }`}
            >
              <span className="text-lg">🚫</span>
              <span className="text-[10px] font-medium leading-tight text-center">No person</span>
            </button>

            {/* Saved persona cards */}
            {savedPersonas.map((persona) => {
              const archetype = persona.archetype ? ARCHETYPE_BY_ID[persona.archetype] : null;
              const emoji = archetype?.emoji ?? "👤";
              const isActive = activePersonaId === persona.id;
              return (
                <div key={persona.id} className="relative shrink-0">
                  <button
                    onClick={() => handleLoadPersona(persona)}
                    data-testid={`persona-card-${persona.id}`}
                    className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-2xl border transition-all min-w-[80px] max-w-[100px] w-full ${
                      isActive
                        ? "bg-primary/10 border-primary/50 ring-2 ring-primary/20"
                        : "bg-background border-border hover:border-primary/30 hover:bg-primary/5"
                    }`}
                  >
                    <span className="text-lg">{emoji}</span>
                    <span className={`text-[10px] font-semibold leading-tight text-center truncate w-full ${isActive ? "text-primary" : "text-foreground"}`}>
                      {persona.name}
                    </span>
                    {archetype && (
                      <span className="text-[9px] text-muted-foreground leading-tight text-center truncate w-full">
                        {archetype.label}
                      </span>
                    )}
                    {persona.lastUsedAt && (
                      <span className="text-[8px] text-muted-foreground/60 leading-tight">
                        {formatDistanceToNow(new Date(persona.lastUsedAt), { addSuffix: true })}
                      </span>
                    )}
                  </button>
                  {/* ⋮ menu */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setPersonaMenuOpenId(personaMenuOpenId === persona.id ? null : persona.id); }}
                    className="absolute top-1 right-1 p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
                    aria-label="More options"
                  >
                    <MoreHorizontal className="w-3 h-3" />
                  </button>
                  {personaMenuOpenId === persona.id && (
                    <div className="absolute top-7 right-0 z-20 bg-card border border-border rounded-xl shadow-lg py-1 min-w-[100px]">
                      <button
                        onClick={() => openEditPersonaModal(persona)}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors flex items-center gap-1.5"
                      >
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                      <button
                        onClick={() => handleDeletePersona(persona.id, persona.name)}
                        className="w-full text-left px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-1.5"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Add person card */}
            <button
              onClick={openNewPersonaModal}
              data-testid="add-person-card"
              className="shrink-0 flex flex-col items-center gap-1 px-3 py-2.5 rounded-2xl border border-dashed border-border text-muted-foreground hover:border-primary/40 hover:text-primary hover:bg-primary/5 transition-all min-w-[72px]"
            >
              <Plus className="w-4 h-4" />
              <span className="text-[10px] font-medium leading-tight text-center">Add person</span>
            </button>
          </div>

          {/* Active persona relationship context badge */}
          {activePersona && (activePersona.powerDynamic || activePersona.communicationStyle || activePersona.familiarityLevel) && (
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {activePersona.powerDynamic && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary border border-border text-muted-foreground">
                  {POWER_DYNAMIC_LABELS[activePersona.powerDynamic]}
                </span>
              )}
              {activePersona.communicationStyle && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary border border-border text-muted-foreground">
                  {COMMUNICATION_STYLE_LABELS[activePersona.communicationStyle]}
                </span>
              )}
              {activePersona.familiarityLevel && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary border border-border text-muted-foreground">
                  {FAMILIARITY_LEVEL_LABELS[activePersona.familiarityLevel]}
                </span>
              )}
              {activePersona.avoidTopics && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-destructive/8 border border-destructive/20 text-destructive/80">
                  Avoid: {activePersona.avoidTopics.slice(0, 40)}{activePersona.avoidTopics.length > 40 ? "…" : ""}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-[1fr_1fr] gap-6 items-start">
          {/* ── Left column: Inputs ── */}
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-3xl p-6 shadow-lg space-y-4">

              {/* Polish mode — draft input */}
              <AnimatePresence initial={false}>
                {isPolish && (
                  <motion.div
                    key="polish"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
                      Your rough draft
                    </label>
                    <Textarea
                      value={polishDraft}
                      onChange={(e) => setPolishDraft(e.target.value)}
                      placeholder="Paste your draft here — doesn't have to be perfect..."
                      className="resize-none min-h-[160px] bg-background/50 border-border rounded-2xl p-4 text-base focus-visible:ring-primary/30"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      The AI will keep your core message but rewrite it in your trained voice.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Received message (reply mode only) */}
              <AnimatePresence initial={false}>
                {!isCompose && !isPolish && (
                  <motion.div
                    key="original"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
                      Message you received
                    </label>
                    <div className="relative">
                      <Textarea
                        value={originalMessage}
                        onChange={(e) => setOriginalMessage(e.target.value)}
                        placeholder="Paste the email or message you need to reply to..."
                        className="resize-none min-h-[130px] bg-background/50 border-border rounded-2xl p-4 pr-12 text-base focus-visible:ring-primary/30"
                        data-testid="original-message-textarea"
                      />
                      <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
                        <AnimatePresence>
                          {originalMessageVoiceInput.isRecording && (
                            <InlineRecordingIndicator
                              recordingTime={originalMessageVoiceInput.recordingTime}
                              formatTime={originalMessageVoiceInput.formatTime}
                            />
                          )}
                        </AnimatePresence>
                        <VoiceInputButton
                          voiceInput={originalMessageVoiceInput}
                          onTranscript={(t) => setOriginalMessage(t)}
                          showLabel
                          label="Speak your idea"
                          data-testid="original-message-voice-button"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Scenario / polish chips (all modes) */}
              <AnimatePresence initial={false}>
                {(
                  <motion.div
                    key="chips"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
                      {isPolish ? "Polish direction" : isCompose ? "Quick start" : "Quick reply"}
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {scenarioChips.map((chip) => (
                        <button
                          key={chip.label}
                          onClick={() => setIntentNote(chip.prompt)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                            intentNote === chip.prompt
                              ? "bg-primary/10 border-primary/50 text-primary"
                              : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                          }`}
                        >
                          {chip.label}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Intent input */}
              <AnimatePresence initial={false}>
                {!isPolish && (
                  <motion.div
                    key="intent"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {isCompose ? "What do you want to write?" : "What you want to say"}
                  </label>
                  <div className="flex items-center gap-0.5 bg-secondary/50 p-0.5 rounded-lg">
                    <button
                      onClick={() => setInputMode("text")}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                        inputMode === "text"
                          ? "bg-card text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Keyboard className="w-3.5 h-3.5" /> Type
                    </button>
                    <button
                      onClick={() => setInputMode("voice")}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                        inputMode === "voice"
                          ? "bg-card text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Mic className="w-3.5 h-3.5" /> Voice
                    </button>
                  </div>
                </div>
                {inputMode === "text" ? (
                  <div className="relative">
                    <Textarea
                      ref={intentRef}
                      value={intentNote}
                      onChange={(e) => setIntentNote(e.target.value)}
                      placeholder={isCompose
                        ? "e.g. 'Cold outreach to a potential client about our design services, keep it brief and warm'"
                        : "e.g. 'Say yes but push the meeting to next week, mention the budget concern'"}
                      className="resize-none min-h-[100px] bg-background/50 border-border rounded-2xl p-4 pr-12 text-base focus-visible:ring-primary/30"
                      data-testid="intent-textarea"
                    />
                    <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
                      <AnimatePresence>
                        {intentVoiceInput.isRecording && (
                          <InlineRecordingIndicator
                            recordingTime={intentVoiceInput.recordingTime}
                            formatTime={intentVoiceInput.formatTime}
                          />
                        )}
                      </AnimatePresence>
                      <VoiceInputButton
                        voiceInput={intentVoiceInput}
                        onTranscript={handleIntentVoiceTranscript}
                        showLabel
                        label="Speak your idea"
                        data-testid="intent-voice-button"
                      />
                    </div>
                  </div>
                ) : (
                  <VoiceRecorder onTranscriptionComplete={handleVoiceTranscription} textareaRef={intentRef} />
                )}
              </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Tone — compact horizontal chips — ALL modes */}
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2.5 block">
                  Tone
                </label>
                <div className="flex flex-wrap gap-2">
                  {activeTones.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => setTone(t.value)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                        tone === t.value
                          ? "bg-primary/10 border-primary/50 text-primary"
                          : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                      }`}
                    >
                      <span>{t.emoji}</span> {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Advanced options — collapsible — ALL modes */}
              <div className="border-t border-border/60 pt-3">
                {(() => {
                  const replyAdvancedCount = [humorLevel, audience, emojiUsage, urgency, formality].filter(Boolean).length + (replyCustomInstruction.trim() ? 1 : 0) + (replyTargetWordCount.trim() ? 1 : 0);
                  const clearReplyAdvanced = () => { setHumorLevel(undefined); setAudience(undefined); setEmojiUsage(undefined); setUrgency(undefined); setFormality(undefined); setReplyCustomInstruction(""); setReplyTargetWordCount(""); };
                  return (
                    <>
                      <button
                        onClick={() => setAdvancedOpen(!advancedOpen)}
                        className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors w-full rounded-lg px-2 py-1.5 ring-1 ring-cyan-500/30 shadow-[0_0_8px_0_rgba(6,182,212,0.18)] hover:ring-cyan-500/50 hover:shadow-[0_0_12px_0_rgba(6,182,212,0.28)]"
                      >
                        <SlidersHorizontal className="w-3.5 h-3.5 text-cyan-500/70" />
                        Format & Length
                        {replyAdvancedCount > 0 && (
                          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-bold">
                            {replyAdvancedCount}
                          </span>
                        )}
                        {advancedOpen ? <ChevronUp className="w-3.5 h-3.5 ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 ml-auto" />}
                      </button>

                      <AnimatePresence initial={false}>
                        {advancedOpen && (
                          <motion.div
                            key="advanced"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="pt-4 space-y-4">

                              {/* Format */}
                              <div>
                                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">Format</label>
                                <div className="flex gap-2">
                                  {FORMATS.map((f) => {
                                    const Icon = f.icon;
                                    return (
                                      <button
                                        key={f.value}
                                        onClick={() => setFormat(f.value)}
                                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border transition-all ${
                                          format === f.value
                                            ? "bg-primary/10 border-primary/40 text-primary"
                                            : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                                        }`}
                                      >
                                        <Icon className="w-3.5 h-3.5" /> {f.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Length */}
                              <div>
                                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">Length</label>
                                <div className="flex gap-2">
                                  {(
                                    [
                                      { value: "short",  label: "Short",  desc: "1-3 sentences" },
                                      { value: "medium", label: "Medium", desc: "1-2 paragraphs" },
                                      { value: "long",   label: "Long",   desc: "Full response" },
                                    ] as { value: GenerateSmartReplyRequestReplyLength; label: string; desc: string }[]
                                  ).map((l) => (
                                    <button
                                      key={l.value}
                                      data-testid={`reply-length-${l.value}`}
                                      onClick={() => setReplyLength(l.value)}
                                      className={`flex-1 flex flex-col items-center px-2 py-2 rounded-xl text-center border transition-all ${
                                        replyLength === l.value
                                          ? "bg-primary/10 border-primary/40 text-primary"
                                          : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                                      }`}
                                    >
                                      <span className="text-xs font-semibold">{l.label}</span>
                                      <span className="text-[10px] opacity-60 mt-0.5">{l.desc}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* Humor Level */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Humor Level</label>
                                  {humorLevel && <button onClick={() => setHumorLevel(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
                                </div>
                                <div className="flex gap-1.5">
                                  {(
                                    [
                                      { value: "none",    label: "None",    desc: "Serious" },
                                      { value: "dry-wit", label: "Dry wit",  desc: "Subtle" },
                                      { value: "witty",   label: "Witty",   desc: "Clever" },
                                      { value: "playful", label: "Playful", desc: "Fun" },
                                    ] as { value: GenerateSmartReplyRequestHumorLevel; label: string; desc: string }[]
                                  ).map((h) => (
                                    <button
                                      key={h.value}
                                      onClick={() => setHumorLevel(humorLevel === h.value ? undefined : h.value)}
                                      className={`flex-1 flex flex-col items-center px-2 py-2 rounded-xl text-center border transition-all ${
                                        humorLevel === h.value
                                          ? "bg-primary/10 border-primary/40 text-primary"
                                          : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                                      }`}
                                    >
                                      <span className="text-xs font-semibold">{h.label}</span>
                                      <span className="text-[10px] opacity-60 mt-0.5">{h.desc}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* Audience */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Audience</label>
                                  {audience && <button onClick={() => setAudience(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
                                </div>
                                <div className="flex gap-1.5">
                                  {(
                                    [
                                      { value: "general",      label: "General",     desc: "Anyone" },
                                      { value: "professional", label: "Professional", desc: "Industry" },
                                      { value: "executive",    label: "Executive",   desc: "C-Suite" },
                                      { value: "friends",      label: "Friends",     desc: "Personal" },
                                    ] as { value: GenerateSmartReplyRequestAudience; label: string; desc: string }[]
                                  ).map((a) => (
                                    <button
                                      key={a.value}
                                      onClick={() => setAudience(audience === a.value ? undefined : a.value)}
                                      className={`flex-1 flex flex-col items-center px-2 py-2 rounded-xl text-center border transition-all ${
                                        audience === a.value
                                          ? "bg-primary/10 border-primary/40 text-primary"
                                          : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                                      }`}
                                    >
                                      <span className="text-xs font-semibold">{a.label}</span>
                                      <span className="text-[10px] opacity-60 mt-0.5">{a.desc}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* Emoji Usage */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Emoji Usage</label>
                                  {emojiUsage && <button onClick={() => setEmojiUsage(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
                                </div>
                                <div className="flex gap-1.5">
                                  {(
                                    [
                                      { value: "off",        label: "Off",        desc: "Zero" },
                                      { value: "minimal",    label: "Minimal",    desc: "1–2" },
                                      { value: "natural",    label: "Natural",    desc: "As needed" },
                                      { value: "expressive", label: "Expressive", desc: "Frequent" },
                                    ] as { value: GenerateSmartReplyRequestEmojiUsage; label: string; desc: string }[]
                                  ).map((e) => (
                                    <button
                                      key={e.value}
                                      onClick={() => setEmojiUsage(emojiUsage === e.value ? undefined : e.value)}
                                      className={`flex-1 flex flex-col items-center px-2 py-2 rounded-xl text-center border transition-all ${
                                        emojiUsage === e.value
                                          ? "bg-primary/10 border-primary/40 text-primary"
                                          : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                                      }`}
                                    >
                                      <span className="text-xs font-semibold">{e.label}</span>
                                      <span className="text-[10px] opacity-60 mt-0.5">{e.desc}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* Urgency */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Urgency</label>
                                  {urgency && <button onClick={() => setUrgency(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
                                </div>
                                <div className="flex gap-1.5">
                                  {(
                                    [
                                      { value: "relaxed", label: "Relaxed", desc: "No rush" },
                                      { value: "normal",  label: "Normal",  desc: "In due course" },
                                      { value: "urgent",  label: "Urgent",  desc: "Act fast" },
                                    ] as { value: GenerateSmartReplyRequestUrgency; label: string; desc: string }[]
                                  ).map((u) => (
                                    <button
                                      key={u.value}
                                      onClick={() => setUrgency(urgency === u.value ? undefined : u.value)}
                                      className={`flex-1 flex flex-col items-center px-2 py-2 rounded-xl text-center border transition-all ${
                                        urgency === u.value
                                          ? "bg-primary/10 border-primary/40 text-primary"
                                          : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                                      }`}
                                    >
                                      <span className="text-xs font-semibold">{u.label}</span>
                                      <span className="text-[10px] opacity-60 mt-0.5">{u.desc}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* Formality */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Formality</label>
                                  {formality && <button onClick={() => setFormality(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
                                </div>
                                <div className="flex gap-1.5">
                                  {(
                                    [
                                      { value: "casual",       label: "Casual",       desc: "Like texting" },
                                      { value: "professional", label: "Professional", desc: "Business" },
                                      { value: "formal",       label: "Formal",       desc: "Elevated" },
                                    ] as { value: GenerateSmartReplyRequestFormality; label: string; desc: string }[]
                                  ).map((f) => (
                                    <button
                                      key={f.value}
                                      onClick={() => setFormality(formality === f.value ? undefined : f.value)}
                                      className={`flex-1 flex flex-col items-center px-2 py-2 rounded-xl text-center border transition-all ${
                                        formality === f.value
                                          ? "bg-primary/10 border-primary/40 text-primary"
                                          : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                                      }`}
                                    >
                                      <span className="text-xs font-semibold">{f.label}</span>
                                      <span className="text-[10px] opacity-60 mt-0.5">{f.desc}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* Target Word Count */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Target Word Count</label>
                                  {replyTargetWordCount.trim() && (
                                    <button onClick={() => setReplyTargetWordCount("")} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>
                                  )}
                                </div>
                                <input
                                  type="number"
                                  min={5}
                                  max={2000}
                                  value={replyTargetWordCount}
                                  onChange={(e) => setReplyTargetWordCount(e.target.value)}
                                  placeholder="e.g. 150"
                                  className="w-full text-xs bg-background border border-border rounded-xl p-3 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
                                />
                                <p className="text-[10px] text-muted-foreground mt-1">AI will aim for exactly this many words (±10%)</p>
                              </div>

                              {/* Custom Instruction */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Special instruction</label>
                                  {replyCustomInstruction.trim() && (
                                    <button onClick={() => setReplyCustomInstruction("")} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>
                                  )}
                                </div>
                                <textarea
                                  value={replyCustomInstruction}
                                  onChange={(e) => setReplyCustomInstruction(e.target.value.slice(0, 300))}
                                  placeholder="e.g. keep it under 3 sentences and end with a question…"
                                  rows={2}
                                  className="w-full text-xs bg-background border border-border rounded-xl p-3 text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
                                />
                                <p className="text-[10px] text-muted-foreground mt-1 text-right">{replyCustomInstruction.length}/300</p>
                              </div>

                              <div className="flex items-center gap-3 pt-1">
                                <button
                                  onClick={openNewPersonaModal}
                                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                                >
                                  <Users className="w-3.5 h-3.5" />
                                  Save as person
                                </button>
                                {replyAdvancedCount > 0 && (
                                  <>
                                    <span className="text-border">·</span>
                                    <button
                                      onClick={clearReplyAdvanced}
                                      className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                                    >
                                      Clear all
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  );
                })()}
              </div>

              {/* Generate button */}
              <Button
                onClick={handleGenerate}
                disabled={isLoading || !canGenerate}
                className="w-full h-13 rounded-xl text-base font-bold bg-gradient-to-r from-primary to-accent hover:opacity-90 text-primary-foreground shadow-lg shadow-primary/25 transition-all"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Send className="w-5 h-5 mr-2" />
                )}
                {isLoading
                  ? (isPolish ? "Polishing your draft..." : isCompose ? "Drafting your message..." : "Writing your reply...")
                  : (isPolish ? "Polish Draft" : isCompose ? "Generate Message" : "Generate Reply")}
              </Button>
            </div>
          </div>

          {/* ── Right column: Output ── */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex flex-col gap-5"
          >
            <div className="bg-card border border-border rounded-3xl p-6 shadow-xl flex flex-col min-h-[440px]">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-lg flex items-center gap-2">
                  <Sparkles className="w-4.5 h-4.5 text-primary" />
                  {isCompose ? "Your Message" : "Your Reply"}
                </h2>
                {result && !isLoading && (
                  <button
                    onClick={() => {
                      setIsEditing(!isEditing);
                      if (isEditing) setEditedReply(result.generatedReply);
                    }}
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                    aria-label={isEditing ? "Cancel edit" : "Edit reply"}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="flex-1 min-h-[220px]">
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center h-full gap-4">
                    <div className="relative w-16 h-16">
                      <div className="absolute inset-0 rounded-full bg-primary/10 animate-ping" />
                      <div className="relative w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <Loader2 className="w-7 h-7 text-primary animate-spin" />
                      </div>
                    </div>
                    <p className="text-muted-foreground font-medium animate-pulse text-sm">
                      {isCompose ? "Drafting your message..." : "Writing a perfect reply..."}
                    </p>
                  </div>
                ) : result ? (
                  isEditing ? (
                    <Textarea
                      value={editedReply}
                      onChange={(e) => setEditedReply(e.target.value)}
                      className="resize-none min-h-[260px] bg-background/50 border-border rounded-2xl p-4 text-base"
                    />
                  ) : (
                    <div className="whitespace-pre-wrap text-base leading-relaxed text-foreground">
                      {editedReply || result.generatedReply}
                    </div>
                  )
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground/50 gap-3 px-4">
                    <div className="w-16 h-16 rounded-2xl bg-secondary/50 flex items-center justify-center">
                      <MessageSquare className="w-8 h-8" />
                    </div>
                    <div>
                      <p className="font-semibold text-base">Your reply will appear here</p>
                      <p className="text-sm mt-1">
                        {isCompose
                          ? "Describe what you want to write and hit Generate"
                          : "Paste a message, jot what you want to say, hit Generate"}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <AnimatePresence>
                {result && !isLoading && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="pt-4 border-t border-border mt-4 space-y-3"
                  >
                    {/* Metadata chips */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] text-muted-foreground bg-secondary px-2 py-1 rounded-lg capitalize">
                        {result.tone}
                      </span>
                      <span className="text-[11px] text-muted-foreground bg-secondary px-2 py-1 rounded-lg">
                        {result.format === "formal_letter" ? "Formal Letter" : result.format === "chat" ? "Chat" : "Email"}
                      </span>
                      {activePersona && (
                        <span className="text-[11px] text-muted-foreground bg-primary/8 border border-primary/20 px-2 py-1 rounded-lg flex items-center gap-1">
                          {activePersona.archetype ? ARCHETYPE_BY_ID[activePersona.archetype]?.emoji : "👤"}
                          {activePersona.name}
                        </span>
                      )}
                    </div>

                    {/* Copy button */}
                    <Button
                      onClick={() => handleCopy(editedReply || result.generatedReply)}
                      className="w-full rounded-xl h-11 bg-primary hover:bg-primary/90"
                    >
                      {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                      {copied ? "Copied!" : "Copy Reply"}
                    </Button>

                    {/* Feedback */}
                    <AnimatePresence>
                      {!feedbackGiven && !feedbackDismissed && (
                        <motion.div
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 4 }}
                          className="flex items-center justify-center gap-3"
                          data-testid="reply-feedback-row"
                        >
                          <span className="text-xs text-muted-foreground">Does this sound like you?</span>
                          <button
                            onClick={() => handleFeedback("up")}
                            data-testid="reply-feedback-up"
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs text-muted-foreground border border-border hover:bg-green-500/10 hover:text-green-400 hover:border-green-500/30 transition-all"
                            disabled={feedbackMutation.isPending}
                          >
                            <ThumbsUp className="w-3 h-3" /> Yes
                          </button>
                          <button
                            onClick={() => handleFeedback("down")}
                            data-testid="reply-feedback-down"
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs text-muted-foreground border border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 transition-all"
                            disabled={feedbackMutation.isPending}
                          >
                            <ThumbsDown className="w-3 h-3" /> Not quite
                          </button>
                          <button
                            onClick={() => setFeedbackDismissed(true)}
                            data-testid="reply-feedback-dismiss"
                            aria-label="Dismiss feedback"
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors leading-none"
                          >
                            ×
                          </button>
                        </motion.div>
                      )}
                      {feedbackGiven && feedbackMessage && (
                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="text-center text-xs text-muted-foreground"
                          data-testid="reply-feedback-message"
                        >
                          {feedbackGiven === "up" ? "👍 " : "👎 "}{feedbackMessage}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Reply History */}
            <div className="bg-card border border-border rounded-3xl shadow-lg overflow-hidden">
              <button
                onClick={() => setHistoryOpen(!historyOpen)}
                className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-secondary/30 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <History className="w-4 h-4 text-muted-foreground" />
                  <span className="font-semibold text-sm">Recent Replies</span>
                  {historyData?.total != null && historyData.total > 0 && (
                    <span className="text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">
                      {historyData.total}
                    </span>
                  )}
                </div>
                {historyOpen
                  ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  : <ChevronDown className="w-4 h-4 text-muted-foreground" />
                }
              </button>

              <AnimatePresence>
                {historyOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    {!historyData?.replies?.length ? (
                      <div className="px-6 pb-5 text-sm text-muted-foreground text-center py-4">
                        No replies yet — generate your first one!
                      </div>
                    ) : (
                      <div className="divide-y divide-border">
                        {historyData.replies.map((reply) => (
                          <div key={reply.id} className="px-5 py-3 flex items-start gap-3 hover:bg-secondary/20 transition-colors">
                            <button
                              className="flex-1 text-left min-w-0"
                              onClick={() => handleHistoryItemClick(reply)}
                            >
                              <p className="text-[11px] font-semibold text-muted-foreground mb-0.5">
                                {reply.originalMessage ? "Re:" : "Compose:"}
                              </p>
                              <p className="text-sm text-foreground truncate">{reply.originalMessage || reply.intentNote}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[10px] text-muted-foreground capitalize bg-secondary px-1.5 py-0.5 rounded">
                                  {reply.tone}
                                </span>
                                <span className="text-[10px] text-muted-foreground">
                                  {formatDistanceToNow(new Date(reply.createdAt ?? Date.now()), { addSuffix: true })}
                                </span>
                              </div>
                            </button>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={() => handleCopy(reply.generatedReply)}
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                                aria-label="Copy reply"
                              >
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => deleteMutation.mutate({ replyId: reply.id ?? "" })}
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                aria-label="Delete reply"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </>
  );
}
